import axiosInstance from './axios';
import { getAllTransactions, removeTransaction } from './offline-queue';
import { getDB, STORES, saveProducts, type SyncMetadata } from './db';
import type { Product } from '../types';
import { OrderType, FulfillmentMethod } from '../types';

/**
 * Result from uploading the offline queue
 */
export interface UploadQueueResult {
  successCount: number;
  failureCount: number;
  totalCount: number;
  results: Array<{
    localId: string;
    success: boolean;
    orderId?: string;
    error?: string;
  }>;
}

/**
 * Result from downloading the product catalog
 */
export interface DownloadCatalogResult {
  productsCount: number;
  inventoryCount: number;
  timestamp: string;
}

/**
 * Response from the products sync endpoint
 * Backend returns { data: Product[], syncTimestamp: string }
 */
interface ProductsSyncResponse {
  data: Product[];
  syncTimestamp: string;
}

/**
 * Upload queued offline orders to the server
 * @param locationId - Optional location ID to filter queued orders
 * @returns Upload results with success/failure counts
 */
export const uploadQueue = async (locationId?: string): Promise<UploadQueueResult> => {
  // Get all queued transactions
  const queuedOrders = await getAllTransactions(locationId);

  // Return early if no orders to sync
  if (queuedOrders.length === 0) {
    return {
      successCount: 0,
      failureCount: 0,
      totalCount: 0,
      results: [],
    };
  }

  // Transform queued orders to sync DTO format
  const orders = queuedOrders.map(order => ({
    localId: order.localId,
    queuedAt: order.queuedAt,
    orderType: OrderType.B2C, // POS orders are always B2C
    locationId: order.locationId,
    customerId: order.customerId,
    fulfillmentMethod: FulfillmentMethod.PICKUP, // POS orders are pickup
    items: order.items.map(item => ({
      productId: item.productId,
      quantity: item.quantity,
    })),
    paymentMethod: order.paymentMethod,
    notes: order.notes,
  }));

  try {
    // Call bulk sync endpoint
    const response = await axiosInstance.post<UploadQueueResult>('/orders/sync', {
      orders,
    });

    const { results, successCount, failureCount, totalCount } = response.data;

    // Remove successfully synced orders from the queue
    const removePromises = results
      .filter(result => result.success)
      .map(result => removeTransaction(result.localId));

    await Promise.all(removePromises);

    return {
      successCount,
      failureCount,
      totalCount,
      results,
    };
  } catch (error) {
    // If the entire sync request fails, return error result
    throw new Error(
      `Failed to sync offline orders: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Download product catalog and inventory from the server
 * @param locationId - Optional location ID to filter products
 * @returns Download results with counts and timestamp
 */
export const downloadCatalog = async (locationId?: string): Promise<DownloadCatalogResult> => {
  const db = await getDB();

  // Get last sync timestamp from metadata
  const syncKey = locationId ? `products-sync-${locationId}` : 'products-sync';
  let lastSyncAt: string | undefined;

  try {
    const syncMeta = await db.get(STORES.SYNC_META, syncKey);
    if (syncMeta) {
      lastSyncAt = syncMeta.lastSyncAt;
    }
  } catch (error) {
    // Ignore errors reading sync metadata, will do full sync
  }

  // Build query parameters
  const params: Record<string, string> = {};
  if (lastSyncAt) {
    params.lastModified = lastSyncAt;
  }
  if (locationId) {
    params.locationId = locationId;
  }

  try {
    // Call products sync endpoint
    const response = await axiosInstance.get<ProductsSyncResponse>('/products/sync', {
      params,
    });

    const { data: products, syncTimestamp } = response.data;

    // Save products to local database
    // Note: Backend doesn't return inventory - inventory is cached separately via the POS inventory query
    if (products && products.length > 0) {
      await saveProducts(products, locationId);
    }

    // Update sync metadata
    const syncMeta: SyncMetadata = {
      key: syncKey,
      lastSyncAt: syncTimestamp,
      status: 'idle',
    };

    const tx = db.transaction(STORES.SYNC_META, 'readwrite');
    await tx.objectStore(STORES.SYNC_META).put(syncMeta);
    await tx.done;

    return {
      productsCount: products?.length || 0,
      inventoryCount: 0, // Inventory is synced separately via POS inventory query
      timestamp: syncTimestamp,
    };
  } catch (error) {
    // Update sync metadata with error status
    try {
      const syncMeta: SyncMetadata = {
        key: syncKey,
        lastSyncAt: lastSyncAt || new Date().toISOString(),
        status: 'error',
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      };

      const tx = db.transaction(STORES.SYNC_META, 'readwrite');
      await tx.objectStore(STORES.SYNC_META).put(syncMeta);
      await tx.done;
    } catch {
      // Ignore errors updating metadata
    }

    throw new Error(
      `Failed to download product catalog: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

const syncManagerApi = {
  uploadQueue,
  downloadCatalog,
};

export default syncManagerApi;
